import React, { useMemo } from 'react';
import ManCrossPageFooter from '../components/ManCrossPageFooter';

// S15 — IRH Scatter 4 Cuadrantes (Spec V4 §3.4)
// Layout visual (imagen referencia):
//   TOP-LEFT  Q1 CRÍTICA    IASH<60 & IPRA<65  (bQ1) → rojo    #E74C3C
//   TOP-RIGHT Q2 LIDERAZGO  IASH<60 & IPRA≥65  (bQ3) → naranja #E67E22
//   BOT-LEFT  Q3 CONDUCTUAL IASH≥60 & IPRA<65  (bQ2) → amarillo #D4AC0D
//   BOT-RIGHT Q4 SEGURO     IASH≥60 & IPRA≥65  (bQ4) → verde   #62C190
//
// Ejes simétricos:
//   X [30, 100] → umbral 65 al (65-30)/(100-30) = 50%  del ancho ✓
//   Y [20, 100] → umbral 60 al (60-20)/(100-20) = 50%  del alto  ✓
// Y NO invertido (coordenadas SVG nativas):
//   IASH bajo → y pequeño → arriba en SVG → Q1 queda TOP-LEFT ✓

const VQS = [
    { vq: 1, bq: 1, label: 'Q1 CRÍTICA',    bg: '#FFE5E5', color: '#E74C3C', text: '#C0392B', zone: 'ZONA CRÍTICA',       action: 'Intervención individual inmediata esta semana' },
    { vq: 2, bq: 3, label: 'Q2 LIDERAZGO',  bg: '#FFF0D6', color: '#E67E22', text: '#A04000', zone: 'RIESGO DE LIDERAZGO', action: 'Intervenir al supervisor antes que al operario' },
    { vq: 3, bq: 2, label: 'Q3 CONDUCTUAL', bg: '#FFF8D6', color: '#D4AC0D', text: '#7D6608', zone: 'RIESGO CONDUCTUAL',   action: 'Plan de autocuidado por perfil IASH' },
    { vq: 4, bq: 4, label: 'Q4 SEGURO',     bg: '#E8F8F0', color: '#62C190', text: '#1E7A4E', zone: 'ZONA SEGURA',         action: 'Monitoreo preventivo próxima medición' },
];

const DOT_COLOR = { 1: '#E74C3C', 2: '#D4AC0D', 3: '#E67E22', 4: '#62C190' };

// ViewBox: coordenadas absolutas del canvas SVG
const VW = 520, VH = 290;
const PL = 32, PR = 10, PT = 24, PB = 22;
const IW = VW - PL - PR;
const IH = VH - PT - PB;

const X_MIN = 30, X_MAX = 100;
const Y_MIN = 20, Y_MAX = 100;
const toX = (v) => PL + (Math.min(X_MAX, Math.max(X_MIN, v)) - X_MIN) / (X_MAX - X_MIN) * IW;
const toY = (v) => PT + (Math.min(Y_MAX, Math.max(Y_MIN, v)) - Y_MIN) / (Y_MAX - Y_MIN) * IH;

const TX = toX(65);  // = PL + IW/2 (centro exacto)
const TY = toY(60);  // = PT + IH/2 (centro exacto)
const CX_L = (PL + TX) / 2;
const CX_R = (TX + VW - PR) / 2;

const ManCrossPageIRHScatter = ({ reportData, headerText, pageNumber }) => {
    const payload = reportData?.payload || {};
    const scatter = payload.irh_scatter || [];
    const qCounts = payload.irh_quadrant_counts || {};

    const total   = scatter.length;
    const q1Count = qCounts['1'] ?? 0;
    const q2Count = qCounts['3'] ?? 0;  // bq=3 → vq=2 LIDERAZGO
    const q3Count = qCounts['2'] ?? 0;  // bq=2 → vq=3 CONDUCTUAL
    const q4Count = qCounts['4'] ?? 0;

    // Headcount total de planta (para % reales sobre planta, no solo scatter)
    const totalPlant = useMemo(() => {
        const hba = payload.headcount_by_management_area || {};
        const sum = Object.values(hba).reduce((s, n) => s + (typeof n === 'number' ? n : 0), 0);
        return sum > 0 ? sum : total;
    }, [payload.headcount_by_management_area, total]);

    const q1PctPlant = totalPlant > 0 ? (q1Count / totalPlant * 100).toFixed(1) : '0.0';
    const q4PctPlant = totalPlant > 0 ? (q4Count / totalPlant * 100).toFixed(1) : '0.0';

    const vqStats = useMemo(() =>
        VQS.map(vq => ({
            vq:    vq.vq,
            count: qCounts[String(vq.bq)] ?? 0,
            pct:   total > 0 ? ((qCounts[String(vq.bq)] ?? 0) / total * 100).toFixed(1) : '0.0',
        })),
        [qCounts, total]
    );

    // 1 punto cada 10 ops (no-Q1); todos los Q1 siempre (al final = encima)
    const sampledDots = useMemo(() => {
        const q1    = scatter.filter(p => Number(p.cuadrante) === 1);
        const other = scatter.filter(p => Number(p.cuadrante) !== 1);
        return [...other.filter((_, i) => i % 10 === 0), ...q1];
    }, [scatter]);

    return (
        <div className="mgcr-irh-page">

            <div className="mgcr-page-header">
                <span className="mgcr-page-header-text">{headerText}</span>
                <span className="mgcr-page-header-num">{pageNumber}</span>
            </div>

            <div className="keep-blankspace" />

            <div className="mgcr-dx-title-area">
                <h2 className="mgcr-dx-main-title">IRH — Índice de Riesgo Humano</h2>
            </div>

            {/* Board 65 / 35 */}
            <div className="mgcr-irh-board">

                {/* LEFT — scatter */}
                <div className="mgcr-irh-scatter-wrap">
                    <div className="mgcr-irh-scatter-caption">
                        <span className="mgcr-irh-caption-tag">Gráfico de Cuadrantes</span>
                        <span className="mgcr-irh-caption-sep" />
                        <span className="mgcr-irh-caption-text">
                            El riesgo no es solo del operario — es la combinación con quien lo lidera.
                        </span>
                    </div>

                    {/* svg-wrap: width 100% → el SVG escala por aspect ratio del viewBox */}
                    <div className="mgcr-irh-svg-wrap">
                        <svg
                            viewBox={`0 0 ${VW} ${VH}`}
                            preserveAspectRatio="xMidYMid meet"
                            style={{ display: 'block', width: '100%' }}
                        >
                            {/* Q backgrounds */}
                            <rect x={PL}  y={PT}  width={TX - PL}      height={TY - PT}       fill="#FFE5E5" />
                            <rect x={TX}  y={PT}  width={VW - PR - TX} height={TY - PT}       fill="#FFF0D6" />
                            <rect x={PL}  y={TY}  width={TX - PL}      height={VH - PB - TY}  fill="#FFF8D6" />
                            <rect x={TX}  y={TY}  width={VW - PR - TX} height={VH - PB - TY}  fill="#E8F8F0" />

                            {/* Threshold lines */}
                            <line x1={TX}  y1={PT}    x2={TX}      y2={VH - PB} stroke="#BCC9D4" strokeWidth="1" strokeDasharray="4 3" />
                            <line x1={PL}  y1={TY}    x2={VW - PR} y2={TY}      stroke="#BCC9D4" strokeWidth="1" strokeDasharray="4 3" />

                            {/* Threshold value labels */}
                            <text x={TX + 3}  y={PT + 10} fontSize="6.5" fill="#BCC9D4">IPRA=65</text>
                            <text x={PL + 3}  y={TY - 4}  fontSize="6.5" fill="#BCC9D4">IASH=60</text>

                            {/* Zone labels — dentro de cada cuadrante */}
                            <text x={CX_L} y={PT + 13} textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#C0392B" letterSpacing="0.07em">ZONA CRÍTICA</text>
                            <text x={CX_R} y={PT + 13} textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#A04000" letterSpacing="0.07em">RIESGO DE LIDERAZGO</text>
                            <text x={CX_L} y={TY + 13} textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#7D6608" letterSpacing="0.07em">RIESGO CONDUCTUAL</text>
                            <text x={CX_R} y={TY + 13} textAnchor="middle" fontSize="6.5" fontWeight="700" fill="#1E7A4E" letterSpacing="0.07em">ZONA SEGURA</text>

                            {/* Tick marks + labels eje X */}
                            {[30, 40, 50, 60, 65, 70, 80, 90, 100].map(v => (
                                <g key={`xt-${v}`}>
                                    <line x1={toX(v)} y1={VH - PB} x2={toX(v)} y2={VH - PB + 3} stroke="#BCC9D4" strokeWidth="0.8" />
                                    <text x={toX(v)} y={VH - PB + 11} fontSize="6.5" fill="#9AA2AE" textAnchor="middle">{v}</text>
                                </g>
                            ))}

                            {/* Tick marks + labels eje Y */}
                            {[20, 30, 40, 50, 60, 70, 80, 90, 100].map(v => (
                                <g key={`yt-${v}`}>
                                    <line x1={PL - 3} y1={toY(v)} x2={PL} y2={toY(v)} stroke="#BCC9D4" strokeWidth="0.8" />
                                    <text x={PL - 5} y={toY(v) + 2.5} fontSize="6.5" fill="#9AA2AE" textAnchor="end">{v}</text>
                                </g>
                            ))}

                            {/* Axis titles */}
                            <text x={PL + IW / 2} y={VH} fontSize="8.5" fill="#617993" textAnchor="middle">IPRA del Supervisor →</text>
                            <text
                                x={10} y={PT + IH / 2}
                                fontSize="8.5" fill="#617993"
                                textAnchor="middle"
                                transform={`rotate(-90, 10, ${PT + IH / 2})`}
                            >IASH →</text>

                            {/* Q count stats — en el centro de cada cuadrante (fila inferior) */}
                            {vqStats.map(({ vq, count, pct }) => {
                                const cfg = VQS.find(v => v.vq === vq);
                                const x = (vq === 1 || vq === 3) ? CX_L : CX_R;
                                const y = (vq <= 2) ? TY - 8 : VH - PB - 9;
                                return (
                                    <text key={vq} x={x} y={y}
                                          fontSize="8.5" fontWeight="700"
                                          fill={cfg.text} textAnchor="middle">
                                        {cfg.label}: {count} ({pct}%)
                                    </text>
                                );
                            })}

                            {/* Puntos — no-Q1 primero, Q1 encima */}
                            {sampledDots.map((p, i) => {
                                const bq  = Number(p.cuadrante);
                                const isQ1 = bq === 1;
                                return (
                                    <circle key={i}
                                        cx={toX(p.ipra_sup)}
                                        cy={toY(p.iash)}
                                        r={isQ1 ? 5.5 : 4}
                                        fill={DOT_COLOR[bq] || '#8E9BAE'}
                                        fillOpacity={isQ1 ? 0.92 : 0.85}
                                        stroke={isQ1 ? '#C0392B' : '#fff'}
                                        strokeWidth={isQ1 ? 1.8 : 0.8}
                                    />
                                );
                            })}
                        </svg>
                    </div>
                </div>

                {/* RIGHT — panel acción */}
                <div className="mgcr-irh-panel">

                    {/* Fórmula — fondo gris, texto gris (estilo managerial estándar) */}
                    <div className="mgcr-irh-formula">
                        <div className="mgcr-irh-formula-rhs">
                            <div className="mgcr-irh-formula-lhs">IRH =</div>
                            <span>(IASH × 0.40)</span>
                            <span>+ (IPRA<sub>sup</sub> × 0.60)</span>
                        </div>
                    </div>

                    {/* Q cards — fondo sólido semáforo. Q1 con badge verde si count=0 */}
                    {VQS.map(vq => {
                        const stat    = vqStats.find(s => s.vq === vq.vq);
                        const count   = stat?.count ?? 0;
                        const pct   = stat?.pct ?? 0;
                        const isQ1Zero = vq.vq === 1 && count === 0;
                        const countBg  = 'rgba(0,0,0,0.20)';
                        const countTxt = isQ1Zero ? '0' : String(count);
                        return (
                            <div key={vq.vq} className="mgcr-irh-qcard" style={{ background: vq.color }}>
                                <div className="mgcr-irh-qcard-header">
                                    {vq.label}
                                    <span className="mgcr-irh-qcard-count"
                                          style={{ background: countBg }}>
                                        {countTxt} ({pct}%)
                                    </span>
                                </div>
                                <div className="mgcr-irh-qcard-action">{vq.action}</div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="mgcr-insight-footer">
                <strong>Esto implica que:</strong>{' '}
                {q1Count > 0
                    ? <>Los <strong>{q1Count}</strong> operarios en Q1 ({q1PctPlant}% de la planta) no necesitan programa grupal — necesitan intervención individual esta semana.</>
                    : (q2Count > 0 || q3Count > 0)
                        ? (() => {
                            const foco   = q2Count >= q3Count ? q2Count   : q3Count;
                            const nombre = q2Count >= q3Count ? 'Q2 — riesgo de liderazgo' : 'Q3 — riesgo conductual';
                            return <>Ningún operario en zona crítica Q1 — las intervenciones anteriores están funcionando. Próximo foco: <strong>{foco}</strong> operarios en {nombre}.</>;
                          })()
                        : <>Planta en zona segura. <strong>{q4Count}</strong> operarios ({q4PctPlant}%) con liderazgo y autocuidado sobre el umbral. KPI siguiente medición: mantener Q4 ≥ 80%.</>
                }
            </div>

            <div className="keep-blankspace" />
            <ManCrossPageFooter />
        </div>
    );
};

export default ManCrossPageIRHScatter;
